import {
  SNAP_PEEK,
  clamp,
  shouldDismiss,
  snapStateFor,
  snapToNearest,
  type SnapState,
} from './panelSnap';

/**
 * Phase 8 chunk 4 — pointer/touch gesture controller for the mobile
 * detail-panel bottom sheet.
 *
 * The grip element listens for `pointerdown`; document-level
 * `pointermove`/`pointerup` capture the rest of the drag so the user
 * can finish the gesture outside the grip bounds. Output is driven by
 * a CSS custom property (`--mobile-translate-y`) so desktop styles
 * are untouched — the property is only consumed inside the
 * `@media (max-width: 700px)` block in {@link SatDetailPanel}.
 *
 * Pure-math snap/dismiss decisions live in {@link './panelSnap'} so
 * they are Vitest-testable without a DOM.
 */
export class PanelGesture {
  private readonly element: HTMLElement;
  private readonly grip: HTMLElement;
  private readonly onDismiss: () => void;
  private readonly onSnapChange: (state: SnapState) => void;

  private dragging = false;
  private pointerId = -1;
  private startY = 0;
  private startPercent = SNAP_PEEK;
  private currentPercent = SNAP_PEEK;
  private currentState: SnapState = 'peek';

  private readonly boundPointerMove: (e: PointerEvent) => void;
  private readonly boundPointerUp: (e: PointerEvent) => void;

  constructor(
    element: HTMLElement,
    grip: HTMLElement,
    callbacks: {
      onDismiss: () => void;
      onSnapChange?: (state: SnapState) => void;
    },
  ) {
    this.element = element;
    this.grip = grip;
    this.onDismiss = callbacks.onDismiss;
    this.onSnapChange = callbacks.onSnapChange ?? (() => undefined);
    this.boundPointerMove = this.handlePointerMove.bind(this);
    this.boundPointerUp = this.handlePointerUp.bind(this);

    grip.style.touchAction = 'none';
    grip.addEventListener('pointerdown', this.handlePointerDown);
  }

  /**
   * Reset the sheet to its default peek state. Called when a new
   * satellite is selected so each open starts at peek regardless of
   * where the previous user-driven drag left it.
   */
  reset(): void {
    this.currentPercent = SNAP_PEEK;
    this.applySnap('peek');
    this.element.style.removeProperty('--mobile-translate-y');
  }

  destroy(): void {
    this.grip.removeEventListener('pointerdown', this.handlePointerDown);
    document.removeEventListener('pointermove', this.boundPointerMove);
    document.removeEventListener('pointerup', this.boundPointerUp);
    document.removeEventListener('pointercancel', this.boundPointerUp);
  }

  private handlePointerDown = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this.dragging = true;
    this.startY = e.clientY;
    this.startPercent = this.currentPercent;
    this.pointerId = e.pointerId;
    this.element.style.transition = 'none';
    document.addEventListener('pointermove', this.boundPointerMove);
    document.addEventListener('pointerup', this.boundPointerUp);
    document.addEventListener('pointercancel', this.boundPointerUp);
    e.preventDefault();
  };

  private handlePointerMove(e: PointerEvent): void {
    if (!this.dragging || e.pointerId !== this.pointerId) return;
    const height = this.element.offsetHeight;
    if (height === 0) return;
    const deltaY = e.clientY - this.startY;
    const deltaPercent = (deltaY / height) * 100;
    this.currentPercent = clamp(this.startPercent + deltaPercent, 0, 100);
    this.element.style.setProperty('--mobile-translate-y', `${this.currentPercent}%`);
  }

  private handlePointerUp(e: PointerEvent): void {
    if (!this.dragging || e.pointerId !== this.pointerId) return;
    this.dragging = false;
    this.pointerId = -1;
    document.removeEventListener('pointermove', this.boundPointerMove);
    document.removeEventListener('pointerup', this.boundPointerUp);
    document.removeEventListener('pointercancel', this.boundPointerUp);

    this.element.style.transition = '';

    if (shouldDismiss(this.currentPercent)) {
      this.onDismiss();
      return;
    }

    const snap = snapToNearest(this.currentPercent);
    this.currentPercent = snap;
    this.element.style.setProperty('--mobile-translate-y', `${snap}%`);
    this.applySnap(snapStateFor(snap));
  }

  private applySnap(state: SnapState): void {
    if (state === this.currentState) return;
    this.currentState = state;
    this.onSnapChange(state);
  }
}
